
import { createContext, useLayoutEffect, useState } from "react";
import { getWebsite, updateData, updateDataArray } from "../firebase/firebase_functions";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/firebase";

export const ClientContext = createContext(null);

// =============================
// Client pages context provider
// =============================
export default function ClientContextProvider({children}){
    
    // Cookies status to check if user approoved cookies
    const [cookiesApprooved] = useState(()=>{
        const ca = localStorage.getItem("webingo_cookiesApprooved");
        return ca ? JSON.parse(ca) : false;
    })

    // Current website's ID
    const [websiteID] = useState(()=>{
        const curWeb = localStorage.getItem("webingo_currentWebsite");
        return curWeb ? JSON.parse(curWeb).id : null;
    });

    // Current user's UID
    const [userUID] = useState(()=>{
        const u = localStorage.getItem("webingo_currentUserUID");
        return u ? JSON.parse(u): "";
    });

    // A cart key to differ each cart by the websiteID and userUID
    // to perform save of several clients on same machine(PC/mobile/etc.)
    const cartKey = `webingo_cart:${websiteID}:${userUID}`;
    const [cartArr,setCartArr] = useState(()=>{
        const c = localStorage.getItem(cartKey);
        return c ? JSON.parse(c) : [];
    });

    // isBelong =>  A flag that checks if the current user
    // connected is belong to the current website
    // to prevent displaying clients data from websiteX on websiteY
    const [isBelong] = useState(()=>{
        const isB = localStorage.getItem("webingo_isBelong");
        return isB ? JSON.parse(isB) : false;
    });

    // A state the holds the current product in
    // order to present his data on product page and so on
    const [currentProduct,setCurrentProduct] = useState(()=>{
        const p = localStorage.getItem("webingo_currentProduct");
        return p ? JSON.parse(p) : {};
    });

    // A State that holds all the products of a website
    const [productsList] = useState(()=>{
        var products = [];
        const func = async ()=>{
            const ws = localStorage.getItem("webingo_currentWebsite");
            const website = ws ? JSON.parse(ws) : {};
            products = website?.settings?.categories?.flatMap(cat=>cat.products);
        }
        func();
        return products;
    });
    
    // Payment page
    const [payAlertFlag, setPayAlertFlag] = useState(false);
    const [isPaying, setIsPaying] = useState(false);
    
    // Cart behavior
    const [isUpdatingCart, setIsUpdatingCart] = useState(false);

    // Changes cartArr at local on each change of the dependencies
    useLayoutEffect(()=>{
        if(cookiesApprooved && isBelong && websiteID!==null && userUID !== ""){
            localStorage.setItem(cartKey, JSON.stringify(cartArr));
        }
    },[cartArr, cartKey, cookiesApprooved, isBelong, websiteID, userUID])
    

    // Changes currentProduct at local on each change of the dependencies
    useLayoutEffect(()=>{
        if(cookiesApprooved){
            localStorage.setItem("webingo_currentProduct", JSON.stringify(currentProduct));
        }
    },[currentProduct, cookiesApprooved])

    // =====================
    // Payment page behavior
    // =====================
    // Cart list deployment
    const deployCartArr=(_website, setToastMessage, messagesOptions, accent_color)=>{
        try {
            return cartArr?.map((val, inx) => (
                <tr key={`cartItem_inx_${inx}`} className="payment_tr">
                    
                    <td
                    className="payment_td"
                    style={{ border: `1px solid ${accent_color}`, color:accent_color }}
                    >
                    {val.price}
                    ₪
                    </td>
    
                    <td
                    className="payment_td"
                    style={{ border: `1px solid ${accent_color}`, color:accent_color }}
                    >
                        <div className="payment_amount_td">
                            <div className="payment_amount_minusDiv">
                                <button className="payment_amount_minus" style={{color:accent_color}} onClick={() => decreaseAmount(_website, val, setToastMessage, messagesOptions)}>-</button>
                            </div>
                            <span className="payment_amount_value">{val.amount}</span>
                            <div className="payment_amount_plusDiv">
                                {val.hasAmount ? <button className="payment_amount_plus" style={{color:accent_color}} onClick={() => increaseAmount(_website, val, setToastMessage, messagesOptions, false)}>+</button> : <button className="payment_amount_plus"></button>}
                            </div>
                        </div>
                    </td>
    
                    {/* NAME */}
                    <td
                    className="payment_td"
                    style={{ border: `1px solid ${accent_color}`, color:accent_color }}
                    >
                    {val.name}
                    </td>
                </tr>
                ));
        } catch (error) {
            console.error("Error on deploy cart items: ",error)
            setToastMessage(messagesOptions.wrong);
        }
    }

    // Check if product already exists at the cart
    const getCartItem = (cart, productId) => cart.find(item => item?._id === productId);
    
    // Cart item increase amount
    const increaseAmount = async(_website, product, setToastMessage, messagesOptions, toNotify) => {
        
        setIsUpdatingCart(true);
        try {
            const cartItem = getCartItem(cartArr, product?._id);
    
            // ONE INSTANCE PRODUCT
            if (!product?.hasAmount ) {
                // Already in cart → do nothing
                if (cartItem) {
                    setToastMessage(messagesOptions.oneInstanceProduct);
                    return;
                };
    
                // Add once
                setCartArr(prev => [
                    ...prev,
                    { 
                        _id: product._id,
                        categoryID: product.categoryID,
                        amount: 1,
                        stockMax: product.amount,
                        hasAmount: product.hasAmount,
                        company: product.company,
                        name: product.name,
                        price: product.hasDiscount? ((100 - product.disValue)/100)*product.price.toFixed(2) : product.price
                    }
                ]);
                setToastMessage(messagesOptions.productAdded);
                return;
            }
    
            // STOCK PRODUCT
            if ( product?.stockMax=== product.amount || product.amount <= 0) {
                // Out of stock → do nothing
                setToastMessage(messagesOptions.outOfStock);
                return;
            }
    
            // Update cart
            setCartArr(prev => {
                if (!cartItem) {
                    return [
                        ...prev,
                        { 
                            _id: product._id,
                            categoryID: product.categoryID,
                            amount: 1,
                            stockMax: product.amount,
                            hasAmount: product.hasAmount,
                            company: product.company,
                            name: product.name,
                            price: product.hasDiscount? ((100 - product.disValue)/100)*product.price.toFixed(2) : product.price
                        }
                    ];
                }
                if(cartItem.amount === cartItem.stockMax){
                    setToastMessage(messagesOptions.productIncreamentException);
                    return prev;
                }
    
                return prev.map(item =>{
                    return item?._id === product?._id
                        ? { ...item, amount: item.amount + 1 }
                        : item
                    }
                );
            });
    
            if(toNotify) setToastMessage(messagesOptions.productAdded)
        } catch (error) {
            console.error("Error on increase amount: ",error);
            setToastMessage(messagesOptions.wrong);
            setIsUpdatingCart(false);
        } finally{
            setIsUpdatingCart(false);
        }
    }
    
    // Cart item decrease amount
    const decreaseAmount = async(_website, product, setToastMessage, messagesOptions) => {
        try {
            const cartItem = getCartItem(cartArr, product._id);
            if (!cartItem){
                setToastMessage(messagesOptions.productDecreamentException);
                return;
            } 
    
            // ONE INSTANCE PRODUCT
            if (!product.hasAmount) {
                setCartArr(prev => prev.filter(item => item._id !== product._id));
                return;
            }
    
            // STOCK PRODUCT
            setCartArr(prev => {
                if (cartItem.amount === 1) {
                    return prev.filter(item => item._id !== product._id);
                }
    
                return prev.map(item =>
                item._id === product._id
                    ? { ...item, amount: item.amount - 1 }
                    : item
                );
            });
        } catch (error) {
            console.error("Error on decrease amount: ",error);
            setToastMessage(messagesOptions.wrong);
        }
    }

    // New order creation
    const createNewOrder = async(_finalPrice, setToastMessage, messagesOptions)=>{
        try {
            const cart = await Promise.all(cartArr.map((item)=>{
                return {
                    name: item.name,
                    price:(item.price * item.amount),
                    amount:item.amount,
                    company:item.company,
                }
            }))
            const newOrder = {
                _id: Date.now(),
                order:cart,
                status:"בטיפול",
                orderTime: new Date(),
                totalPrice: parseInt(_finalPrice),
            };
            return newOrder;
        } catch (error) {
            console.error("Error on creating new order: ",error);
            setToastMessage(messagesOptions.wrong);
        }
    }

    // Check the payment card expiration date is valid
    const checkExpDate =(getTodayDate, expDate)=>{
        const today = getTodayDate();
        if(today> expDate){
            return false;
        }
        return true;
    }
    // Check payment data given by client
    const validatePayment = async( cardNumber, expiryDate, cvv, getTodayDate )=>{
        try{
            if(!cardNumber || !expiryDate || !cvv || cardNumber==="" || expiryDate==="" || cvv===""){
                return {res:false, msg:"נא מלא את כל השדות תחילה!"};
            }
            const regexCVV = /^\d{3}$/;
            const regexNumber = /^\d{4}\s\d{4}\s\d{4}\s\d{4}$/;
            if(!cardNumber.match(regexNumber)){
                return {res:false, msg:"אנא בדוק את מספר האשראי שהזנת"};
            }
            if(!cvv.match(regexCVV)){
                return {res:false, msg:"CVV חייב להיות בעל 3 ספרות בלבד."};
            }
            if(!checkExpDate(getTodayDate, expiryDate)){
                return {res:false, msg:"תוקף כרטיס האשראי אינו תקין! אנא וודא כי הזנת תאריך בתוקף."};
            }
            return {res:true, msg:"ok"};
        } catch(err){
            console.error(err);
            return {res:false, msg:"משהו השתבש, נסה לרענן את הדף ולנסות שוב"};
        }
    }
    // Handle client payment process
    const handleClientPayment = async(needToUpdatePaymentDetails, getTodayDate, website, currentUser, setCurrentUser, setCurrentWebsite, setProblem, setToastMessage, messagesOptions, isMissingPaymentMethod, isMissingAddress, _finalPrice, newAddress, newCardNum, newExpDate, newCvv)=>{
        
        if(!website || website===null || website===undefined || !currentUser || currentUser===null || currentUser===undefined){
            return false;
        }
        // Only if need to update - check if missing address||paymentMethod.
        // If one of them is missing or both => check that inputs values is valid
        // If not valid => return relevant toast message
        // If everything ok => start the payment process
        if(needToUpdatePaymentDetails){
            if(isMissingAddress && (newAddress==="" || newAddress===null || newAddress===undefined)){
                return false;
            }
            if(isMissingPaymentMethod && (newCardNum==="" || newCardNum===null || newCardNum===undefined || newExpDate==="" || newExpDate===null || newExpDate===undefined || newCvv==="" || newCvv===null || newCvv===undefined)){
                return false;
            }
            if(isMissingAddress){
                const addressRegex = /^[\u0590-\u05FF\s]+ \d+, [\u0590-\u05FF\s]+$/;
                if(!newAddress.match(addressRegex)){
                    setToastMessage("הכתובת שהזנת לא תקינה");
                    return false;
                }
            }
            if(isMissingPaymentMethod){
                const isValid = await validatePayment(newCardNum, newExpDate, newCvv, getTodayDate);
                if(!isValid.res){
                    setToastMessage(isValid.msg)
                    return false;
                };
            }
        }

        // Payment process start
        setIsPaying(true);
        // Create new order,
        // If needed to update details => build the paymentMethod struct
        // And then build the new updated client struct to update
        // If did'nt need to update details => just update the orders array
        try {
            const newOrder = await createNewOrder(_finalPrice, setToastMessage, messagesOptions);

            const checkoutFn = httpsCallable(functions, "checkout");

            const checkoutRes = await checkoutFn({
                websiteId: website.id,
                cart : cartArr.map(item => ({
                    productId: item._id,
                    categoryID: item.categoryID,
                    amount: item.amount
                }))
            });

            if(!checkoutRes){
                setToastMessage(messagesOptions.failed);
                return false;
            } 

            var updatedClient = null;
            if(needToUpdatePaymentDetails){
                const newPaymentMethod = {
                    cardNum: newCardNum,
                    expDate: newExpDate,
                    cvv: newCvv,
                }
                updatedClient = {
                    ...currentUser,
                    address: isMissingAddress ? newAddress : currentUser.address,
                    paymentMethod: isMissingPaymentMethod ? newPaymentMethod : currentUser.paymentMethod,
                    orders: [...currentUser.orders, newOrder],
                }
            }
            else if(!needToUpdatePaymentDetails){
                updatedClient = {
                    ...currentUser,
                    orders: [...currentUser.orders, newOrder],
                }
            }
            const clients = website?.settings?.clients;
            const updatedClientsArray = clients.map(c=>{
                if(c._id=== currentUser._id){
                    return updatedClient;
                }
                return c;
            })
            const res = await updateData(website.id, "settings.clients", updatedClientsArray);
            
            // Check if update success => if no then toast message failed
            // If yes request an update of website at local storage with new data
            // Also update current user with his new data updated
            // Then reset states
            if(!res || updatedClient===null) setToastMessage(messagesOptions.failed);
            else if(res === true && updatedClient !== null){
                const updatedWebsite = await getWebsite("settings.domain", website.settings.domain);
                if(updatedWebsite!== null && updatedWebsite !== undefined) setCurrentWebsite(updatedWebsite);
                setCurrentUser(updatedClient);
                setCartArr([]);
                setIsPaying(false);
                setPayAlertFlag(false);
                setToastMessage(messagesOptions.ok);
                setProblem(`תודה שרכשת אצלנו ב- ${website?.settings?.website_name} נשמח לראותך שוב!`);
            }
            return res;
        } catch (error) {
            console.error("Error on client payment: ",error);
            setToastMessage(messagesOptions.wrong)
            return false;
        } finally{
            setIsPaying(false);
        }
    }

    // ===========
    // Cart handle
    // ===========
    // Count the number of different products on cart
    const counter=(arr)=>{
        try {
            if(arr?.length!==0){
                return <div className='counterDiv'>{arr?.length}</div>
            }
            else{
                return null;
            }
        } catch (error) {
            console.error("Error render counter: ",error);
        }
    }
    
    // ===================
    // Product page handle
    // ===================
    // Handling the movement to the product page on each condition
    const moveToProductPage=(nav, website, params, val, setToastMessage, messagesOptions, isDashboard, setDisplay)=>{
        try {
            if(!cookiesApprooved){return alert("עליך לאשר עוגיות לצורך פעולה זו")}
            params.name = val.name;
            setCurrentProduct(val);
            document.querySelector(".searchbar_input").value = "";
            window.scrollTo({
                top:0,
                'behavior':'smooth'
            });
            return isDashboard ? setDisplay("/productPage") : nav(`/${website?.settings?.domain}/products/${params.name}`);
        } catch (error) {
            console.error("Error on moving to product page: ",error);
            setToastMessage(messagesOptions.pageLoadFailed);
        }
    }

    // ================
    // Jobs page handle
    // ================
    // Calculating the days passed for a job createdAt timestamp
    const getJobDaysPassed =async(timestamp)=>{
        if(!timestamp) return;
        let date;
        if (timestamp.toDate) {
            date = timestamp.toDate();
        } else if (timestamp instanceof Date) {
            date = timestamp;
        } else if (typeof timestamp === "string" || timestamp?.seconds) {
            date = new Date(timestamp);
        } else{
            return {message: "Published today"};
        }
        const now = new Date();
        const diff = now - date;
        const diffDays = Math.floor(diff / (1000 * 60 * 60 * 24));

        if(diffDays <= 0){
            return {message: "Published today"}
        }
        else if(diffDays < 7 && diffDays > 0){
            return {message: `Published ${diffDays}  days ago`};
        }
        else if(diffDays >= 7 && diffDays < 30){
            const daysOfWeeks = parseInt(diffDays % 7);
            const weeks = parseInt(diffDays / 7);
            return {message: `Published ${weeks} weeks and ${daysOfWeeks} days ago`};
        }
        else if(diffDays >= 30 && diffDays < 365){
            const daysOfMonths = parseInt(diffDays % 30);
            const months = parseInt(diffDays / 30);
            return {message: `Published ${months} months and ${daysOfMonths} days ago`};
        }
        else if(diffDays >= 365){
            const years = parseInt(diffDays / 365);
            return {message: `Published about ${years} years ago`};
        }
    }

    const val={
        // Jobs page:
        getJobDaysPassed,
        
        // Product page:
        currentProduct,
        productsList,
        setCurrentProduct,
        moveToProductPage,
        
        // Payment page:
        handleClientPayment,
        deployCartArr,
        payAlertFlag,
        setPayAlertFlag,
        isPaying,
        
        // Cart handle:
        cartArr,
        setCartArr,
        counter,
        increaseAmount,
        isUpdatingCart,
    }
    return(
        <ClientContext.Provider value={val}>
            {children}
        </ClientContext.Provider>
    )
}